import { useEffect, useState } from 'react';
import useRefFn from '@stripcash/ui-kit/utils/hooks/useRefFn';

type ArgumentTypes<F extends Function> = F extends (...args: infer A) => any
  ? A
  : never;

export default function useApp<T extends (...a: any[]) => any>(
  appConstructor: T,
  ...args: ArgumentTypes<T>
): ReturnType<T> extends Promise<unknown>
  ? Nullable<Awaited<ReturnType<T>>>
  : Nullable<ReturnType<T>> {
  const [_, update] = useState({});
  const app = useRefFn<ReturnType<T> | Promise<ReturnType<T>>>(() =>
    appConstructor(...args),
  );

  useEffect(() => {
    if (typeof app.current === 'object' && 'then' in app.current) {
      app.current.then((instance) => {
        instance.run();
        app.current = instance;
        update({});
      });
    } else {
      app.current.run();
    }

    return () => {
      if ('then' in app.current) {
        app.current.then((instance) => instance.terminate());
      } else {
        app.current.terminate();
      }
    };
  }, []);

  return 'then' in app.current ? null : app.current;
}
